import { onUnmounted, ref, type UnwrapRef } from 'vue';
import type { SCChannel } from 'sc-channel';
import type { AxiosPromise, AxiosResponse } from '@libs/http';

import { useSocket } from './useSocket';
import {
  type UseInfiniteQueryConfig2,
  useInfiniteVueQuery,
} from './useInfiniteVueQuery';

export type UseLiveInfiniteQueryConfigV2<T> = UseInfiniteQueryConfig2<T> & {
  fetcher: (args: Record<string, any>) => AxiosPromise<T>;
  channel?: string;
  unsubscribe?: boolean;
  getChannels?: (data: AxiosResponse<T> | null) => Array<string> | void;
  onUpdate: (signal: { code: number; data: object }) => void;
  onKeyUnsubscribe?: boolean;
  unsubscribeChannels?: string[];
};

export function useLiveInfiniteQueryV2<
  QueryType extends { items: UnwrapRef<ItemType[]> | null },
  ItemType
>({
  fetcher,
  onUpdate,
  channel,
  unsubscribe = true,
  getChannels,
  ...options
}: UseLiveInfiniteQueryConfigV2<QueryType>): ReturnType<
  typeof useInfiniteVueQuery<QueryType, ItemType>
> {
  const channelsRef = ref<Record<string, SCChannel>>({});
  const { socket, onReady } = useSocket();

  const query = useInfiniteVueQuery<QueryType, ItemType>(fetcher, {
    ...options,
    onSuccess: (result) => {
      options.onSuccess?.(result);

      onReady(() => {
        const channels = channel ? [channel] : getChannels?.(result);

        if (!channels) {
          return;
        }

        for (const name of channels) {
          if (socket.value && !channelsRef.value[name]) {
            channelsRef.value[name] = socket.value.subscribe(name, {
              batch: true,
            });

            if (typeof onUpdate === 'function') {
              channelsRef.value[name].watch(onUpdate);
            }
          }
        }
      });
    },
  });

  function handleUnsubscribe() {
    for (const [channelName, channel] of Object.entries(channelsRef.value)) {
      if (unsubscribe) {
        channel.unsubscribe();
      }

      delete channelsRef.value[channelName];
      channel.unwatch(onUpdate);
    }
  }

  onUnmounted(handleUnsubscribe);

  return { ...query };
}
